<div class="relative bg-white rounded-[20px] overflow-hidden flex flex-col max-h-[90vh]">
  <!-- Close button -->
  <button
    type="button"
    (click)="close()"
    [attr.aria-label]="'bookDemoDialog.close' | translate"
    class="absolute top-4 end-4 z-20 w-9 h-9 grid place-items-center rounded-full bg-[#0614300D] hover:bg-[#0614301A] text-[#061430] transition-colors"
  >
    <app-icon name="tabler:x" [size]="18" />
  </button>

  <!-- Progress bar -->
  <div class="absolute top-0 inset-x-0 h-1 bg-[#0614300D] z-10">
    <div
      class="h-full bg-[#353CD8] transition-all duration-300 ease-out"
      [style.width]="step === 1 ? '33%' : step === 2 ? '66%' : '100%'"
    ></div>
  </div>

  <!-- Step 1: Intent -->
  <div *ngIf="step === 1" class="flex flex-col flex-1 min-h-0">
    <div class="px-6 sm:px-10 pt-10 pb-5 flex-shrink-0">
      <span class="inline-block text-[10px] font-bold uppercase tracking-[2px] text-[#353CD8] mb-3">
        {{ 'bookDemoDialog.step1Label' | translate }}
      </span>
      <h2 class="text-[24px] sm:text-[30px] leading-[1.2] font-[900] tracking-[-1px] text-[#061430] mb-2">
        {{ 'bookDemoDialog.step1Title' | translate }}
      </h2>
      <p class="text-[15px] leading-[22px] text-[#3B4A6B]">
        {{ 'bookDemoDialog.step1Subtitle' | translate }}
      </p>
    </div>

    <form #stepForm [formGroup]="form" class="flex flex-col gap-5 px-6 sm:px-10 pb-6 flex-1 overflow-y-auto">
      <!-- Intent -->
      <div data-field="intent">
        <label class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.intentLabel' | translate }}
        </label>
        <div class="flex flex-col gap-2">
          <button
            *ngFor="let option of intents"
            type="button"
            (click)="selectIntent(option.value)"
            class="flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-start transition-all"
            [class.border-[#353CD8]]="form.controls.intent.value === option.value"
            [class.bg-[#353CD80A]]="form.controls.intent.value === option.value"
            [class.border-[rgba(6,20,48,0.12)]]="form.controls.intent.value !== option.value"
            [class.hover:border-[rgba(6,20,48,0.25)]]="form.controls.intent.value !== option.value"
          >
            <span
              class="mt-0.5 grid place-items-center w-5 h-5 rounded-full border-2 shrink-0 transition-colors"
              [class.border-[#353CD8]]="form.controls.intent.value === option.value"
              [class.border-[rgba(6,20,48,0.25)]]="form.controls.intent.value !== option.value"
            >
              <span
                *ngIf="form.controls.intent.value === option.value"
                class="w-2.5 h-2.5 rounded-full bg-[#353CD8]"
              ></span>
            </span>
            <span class="flex flex-col flex-1 min-w-0">
              <span class="text-[14px] font-bold text-[#061430] leading-[18px]">
                {{ option.titleKey | translate }}
              </span>
              <span class="text-[12px] text-[#838A98] leading-[16px] mt-0.5">
                {{ option.descKey | translate }}
              </span>
            </span>
          </button>
        </div>
        <p
          *ngIf="form.controls.intent.touched && form.controls.intent.invalid"
          class="mt-2 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.intentError' | translate }}
        </p>
      </div>

      <!-- Job seeker notice -->
      <div
        *ngIf="isJobSeeker"
        class="rounded-2xl border border-[#FFE9BD] bg-[#FFF8EA] px-4 py-3.5 flex items-start gap-3"
      >
        <span class="mt-0.5 grid place-items-center text-[#B4690B] shrink-0">
          <app-icon name="tabler:alert-triangle" [size]="18" />
        </span>
        <p class="text-[13px] leading-[18px] text-[#5A3A0A]">
          {{ 'bookDemoDialog.jobSeekerNoticeBefore' | translate }}<a
            [href]="jobSeekerUrl"
            target="_blank"
            rel="noopener"
            class="font-semibold text-[#353CD8] underline hover:text-[#2930b8]"
          >{{ 'bookDemoDialog.jobSeekerNoticeLink' | translate }}</a>{{ 'bookDemoDialog.jobSeekerNoticeAfter' | translate }}
        </p>
      </div>

      <!-- Expected hires (conditional) -->
      <div *ngIf="showExpectedHires" data-field="expectedHires">
        <label class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.hiresLabel' | translate }}
        </label>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            *ngFor="let range of hireRanges"
            type="button"
            (click)="selectHireRange(range)"
            class="rounded-xl border px-3 py-2.5 text-[14px] font-semibold transition-all"
            [class.border-[#353CD8]]="form.controls.expectedHires.value === range"
            [class.bg-[#353CD8]]="form.controls.expectedHires.value === range"
            [class.text-white]="form.controls.expectedHires.value === range"
            [class.border-[rgba(6,20,48,0.12)]]="form.controls.expectedHires.value !== range"
            [class.text-[#061430]]="form.controls.expectedHires.value !== range"
            [class.hover:border-[rgba(6,20,48,0.25)]]="form.controls.expectedHires.value !== range"
          >
            {{ range }}
          </button>
        </div>
        <p
          *ngIf="form.controls.expectedHires.touched && form.controls.expectedHires.invalid"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.hiresError' | translate }}
        </p>
      </div>
    </form>

    <!-- Sticky footer with Continue -->
    <div class="px-6 sm:px-10 py-4 border-t border-[rgba(6,20,48,0.06)] bg-white flex-shrink-0">
      <button
        type="button"
        (click)="goToStep2()"
        [disabled]="isJobSeeker"
        class="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#353CD8] px-7 py-3.5 text-[15px] font-bold text-white shadow-[0_4px_16px_0_rgba(53,60,216,0.3)] transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
      >
        {{ 'bookDemoDialog.continue' | translate }}
        <span class="rtl:flip inline-grid place-items-center">
          <app-icon name="tabler:arrow-narrow-right" [size]="18" />
        </span>
      </button>
    </div>
  </div>

  <!-- Step 2: Personal info -->
  <div *ngIf="step === 2" class="flex flex-col flex-1 min-h-0">
    <div class="px-6 sm:px-10 pt-10 pb-5 flex-shrink-0 flex items-start gap-4">
      <button
        type="button"
        (click)="back()"
        class="mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0614300D] hover:bg-[#0614301A] text-[#061430] transition-colors shrink-0"
        [attr.aria-label]="'bookDemoDialog.back' | translate"
      >
        <span class="rtl:flip inline-grid place-items-center">
          <app-icon name="tabler:arrow-narrow-left" [size]="16" />
        </span>
      </button>
      <div class="flex flex-col">
        <span class="inline-block text-[10px] font-bold uppercase tracking-[2px] text-[#353CD8] mb-3">
          {{ 'bookDemoDialog.step2Label' | translate }}
        </span>
        <h2 class="text-[24px] sm:text-[30px] leading-[1.2] font-[900] tracking-[-1px] text-[#061430] mb-2">
          {{ 'bookDemoDialog.step2Title' | translate }}
        </h2>
        <p class="text-[15px] leading-[22px] text-[#3B4A6B]">
          {{ 'bookDemoDialog.step2Subtitle' | translate }}
        </p>
      </div>
    </div>

    <form #stepForm [formGroup]="form" class="flex flex-col gap-7 px-6 sm:px-10 pb-6 flex-1 overflow-y-auto">
      <!-- Audience -->
      <div data-field="audience">
        <label class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.audienceLabel' | translate }}
        </label>
        <div class="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            (click)="selectAudience('company')"
            class="group flex items-center gap-2.5 rounded-2xl border px-4 py-3.5 text-start transition-all"
            [class.border-[#353CD8]]="form.controls.audience.value === 'company'"
            [class.bg-[#353CD80A]]="form.controls.audience.value === 'company'"
            [class.border-[rgba(6,20,48,0.12)]]="form.controls.audience.value !== 'company'"
            [class.hover:border-[rgba(6,20,48,0.25)]]="form.controls.audience.value !== 'company'"
          >
            <span
              class="grid place-items-center w-8 h-8 rounded-full shrink-0"
              [class.bg-[#353CD8]]="form.controls.audience.value === 'company'"
              [class.text-white]="form.controls.audience.value === 'company'"
              [class.bg-[#0614300D]]="form.controls.audience.value !== 'company'"
              [class.text-[#061430]]="form.controls.audience.value !== 'company'"
            >
              <app-icon name="tabler:building" [size]="16" />
            </span>
            <span class="flex flex-col flex-1 min-w-0">
              <span class="text-[14px] font-bold text-[#061430] leading-[18px]">
                {{ 'bookDemoDialog.audience.company' | translate }}
              </span>
              <span class="text-[12px] text-[#838A98] leading-[16px]">
                {{ 'bookDemoDialog.audience.companyHint' | translate }}
              </span>
            </span>
          </button>
          <button
            type="button"
            (click)="selectAudience('individual')"
            class="group flex items-center gap-2.5 rounded-2xl border px-4 py-3.5 text-start transition-all"
            [class.border-[#353CD8]]="form.controls.audience.value === 'individual'"
            [class.bg-[#353CD80A]]="form.controls.audience.value === 'individual'"
            [class.border-[rgba(6,20,48,0.12)]]="form.controls.audience.value !== 'individual'"
            [class.hover:border-[rgba(6,20,48,0.25)]]="form.controls.audience.value !== 'individual'"
          >
            <span
              class="grid place-items-center w-8 h-8 rounded-full shrink-0"
              [class.bg-[#353CD8]]="form.controls.audience.value === 'individual'"
              [class.text-white]="form.controls.audience.value === 'individual'"
              [class.bg-[#0614300D]]="form.controls.audience.value !== 'individual'"
              [class.text-[#061430]]="form.controls.audience.value !== 'individual'"
            >
              <app-icon name="tabler:user" [size]="16" />
            </span>
            <span class="flex flex-col flex-1 min-w-0">
              <span class="text-[14px] font-bold text-[#061430] leading-[18px]">
                {{ 'bookDemoDialog.audience.individual' | translate }}
              </span>
              <span class="text-[12px] text-[#838A98] leading-[16px]">
                {{ 'bookDemoDialog.audience.individualHint' | translate }}
              </span>
            </span>
          </button>
        </div>
      </div>

      <!-- First + last name -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div data-field="firstName">
          <label for="bookDemoFirstName" class="block text-[13px] font-semibold text-[#061430] mb-2">
            {{ 'bookDemoDialog.firstNameLabel' | translate }}
          </label>
          <input
            id="bookDemoFirstName"
            type="text"
            autocomplete="given-name"
            formControlName="firstName"
            [placeholder]="'bookDemoDialog.firstNamePlaceholder' | translate"
            class="w-full rounded-xl border border-[rgba(6,20,48,0.12)] bg-white px-4 py-3 text-[14px] text-[#061430] placeholder:text-[#838A98] outline-none transition-colors focus:border-[#353CD8] focus:ring-2 focus:ring-[#353CD81A]"
            [class.border-[#FF6767]]="form.controls.firstName.touched && form.controls.firstName.invalid"
          />
          <p
            *ngIf="form.controls.firstName.touched && form.controls.firstName.invalid"
            class="mt-1.5 text-[12px] text-[#FF6767]"
          >
            {{ 'bookDemoDialog.firstNameError' | translate }}
          </p>
        </div>
        <div data-field="lastName">
          <label for="bookDemoLastName" class="block text-[13px] font-semibold text-[#061430] mb-2">
            {{ 'bookDemoDialog.lastNameLabel' | translate }}
          </label>
          <input
            id="bookDemoLastName"
            type="text"
            autocomplete="family-name"
            formControlName="lastName"
            [placeholder]="'bookDemoDialog.lastNamePlaceholder' | translate"
            class="w-full rounded-xl border border-[rgba(6,20,48,0.12)] bg-white px-4 py-3 text-[14px] text-[#061430] placeholder:text-[#838A98] outline-none transition-colors focus:border-[#353CD8] focus:ring-2 focus:ring-[#353CD81A]"
            [class.border-[#FF6767]]="form.controls.lastName.touched && form.controls.lastName.invalid"
          />
          <p
            *ngIf="form.controls.lastName.touched && form.controls.lastName.invalid"
            class="mt-1.5 text-[12px] text-[#FF6767]"
          >
            {{ 'bookDemoDialog.lastNameError' | translate }}
          </p>
        </div>
      </div>

      <!-- Company name -->
      <div *ngIf="showCompanyName" data-field="companyName">
        <label for="companyName" class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.companyNameLabel' | translate }}
        </label>
        <input
          id="companyName"
          type="text"
          formControlName="companyName"
          [placeholder]="'bookDemoDialog.companyNamePlaceholder' | translate"
          class="w-full rounded-xl border border-[rgba(6,20,48,0.12)] bg-white px-4 py-3 text-[14px] text-[#061430] placeholder:text-[#838A98] outline-none transition-colors focus:border-[#353CD8] focus:ring-2 focus:ring-[#353CD81A]"
          [class.border-[#FF6767]]="form.controls.companyName.touched && form.controls.companyName.invalid"
        />
        <p
          *ngIf="form.controls.companyName.touched && form.controls.companyName.invalid"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.companyNameError' | translate }}
        </p>
      </div>

      <!-- Email -->
      <div data-field="email">
        <label for="bookDemoEmail" class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.emailLabel' | translate }}
        </label>
        <input
          id="bookDemoEmail"
          type="email"
          autocomplete="email"
          inputmode="email"
          formControlName="email"
          [placeholder]="'bookDemoDialog.emailPlaceholder' | translate"
          class="w-full rounded-xl border border-[rgba(6,20,48,0.12)] bg-white px-4 py-3 text-[14px] text-[#061430] placeholder:text-[#838A98] outline-none transition-colors focus:border-[#353CD8] focus:ring-2 focus:ring-[#353CD81A]"
          [class.border-[#FF6767]]="form.controls.email.touched && form.controls.email.invalid"
        />
        <p
          *ngIf="form.controls.email.touched && form.controls.email.errors?.['required']"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.emailErrorRequired' | translate }}
        </p>
        <p
          *ngIf="form.controls.email.touched && form.controls.email.errors?.['email']"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.emailErrorInvalid' | translate }}
        </p>
      </div>

      <!-- Phone -->
      <div data-field="phone">
        <label for="bookDemoPhone" class="block text-[13px] font-semibold text-[#061430] mb-2">
          {{ 'bookDemoDialog.phoneLabel' | translate }}
        </label>
        <input
          id="bookDemoPhone"
          type="tel"
          autocomplete="tel"
          inputmode="tel"
          formControlName="phone"
          [placeholder]="'bookDemoDialog.phonePlaceholder' | translate"
          class="w-full rounded-xl border border-[rgba(6,20,48,0.12)] bg-white px-4 py-3 text-[14px] text-[#061430] placeholder:text-[#838A98] outline-none transition-colors focus:border-[#353CD8] focus:ring-2 focus:ring-[#353CD81A]"
          [class.border-[#FF6767]]="form.controls.phone.touched && form.controls.phone.invalid"
        />
        <p
          *ngIf="form.controls.phone.touched && form.controls.phone.errors?.['required']"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.phoneErrorRequired' | translate }}
        </p>
        <p
          *ngIf="form.controls.phone.touched && form.controls.phone.errors?.['pattern']"
          class="mt-1.5 text-[12px] text-[#FF6767]"
        >
          {{ 'bookDemoDialog.phoneErrorInvalid' | translate }}
        </p>
      </div>
    </form>

    <!-- Sticky footer with Continue -->
    <div class="px-6 sm:px-10 py-4 border-t border-[rgba(6,20,48,0.06)] bg-white flex-shrink-0">
      <button
        type="button"
        (click)="goToSchedule()"
        [disabled]="submitting"
        class="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#353CD8] px-7 py-3.5 text-[15px] font-bold text-white shadow-[0_4px_16px_0_rgba(53,60,216,0.3)] transition-all hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {{ 'bookDemoDialog.continue' | translate }}
        <span *ngIf="!submitting" class="rtl:flip inline-grid place-items-center">
          <app-icon name="tabler:arrow-narrow-right" [size]="18" />
        </span>
        <span *ngIf="submitting" class="inline-grid place-items-center">
          <app-icon name="tabler:loader-2" [size]="18" class="animate-spin" />
        </span>
      </button>
    </div>
  </div>

  <!-- Step 3: HubSpot meetings embed -->
  <div *ngIf="step === 3" class="flex flex-col h-[640px] max-h-[80vh]">
    <div class="flex items-center gap-3 px-6 sm:px-10 pt-9 pb-4 border-b border-[rgba(6,20,48,0.06)]">
      <button
        type="button"
        (click)="back()"
        class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#3B4A6B] hover:text-[#061430] transition-colors"
      >
        <span class="rtl:flip inline-grid place-items-center">
          <app-icon name="tabler:arrow-narrow-left" [size]="16" />
        </span>
        {{ 'bookDemoDialog.back' | translate }}
      </button>
      <div class="ms-auto flex flex-col items-end">
        <span
          class="inline-block text-[10px] font-bold uppercase tracking-[2px] text-[#353CD8]"
        >
          {{ 'bookDemoDialog.step3Label' | translate }}
        </span>
        <span class="text-[13px] font-semibold text-[#061430]">
          {{ 'bookDemoDialog.scheduleStepTitle' | translate }}
        </span>
      </div>
    </div>

    <div class="relative flex-1 overflow-hidden bg-white">
      <!-- Spinner overlay while HubSpot loads -->
      <div
        *ngIf="hubspotLoading"
        class="absolute inset-0 z-10 grid place-items-center bg-white"
      >
        <div class="flex flex-col items-center gap-3">
          <span class="inline-grid place-items-center text-[#353CD8]">
            <app-icon name="tabler:loader-2" [size]="32" class="animate-spin" />
          </span>
          <span class="text-[13px] text-[#3B4A6B]">
            {{ 'bookDemoDialog.loadingSchedule' | translate }}
          </span>
        </div>
      </div>

      <iframe
        *ngIf="hubspotUrl"
        [src]="hubspotUrl"
        (load)="onHubspotLoaded()"
        title="Book a demo"
        frameborder="0"
        class="w-full h-full min-h-[560px] border-0"
      ></iframe>
    </div>
  </div>
</div>
